#!/usr/bin/env node
import { Command, Option } from 'commander';
import { printBanner } from './banner.js';
import { productName } from './profiles.js';
import { cliVersion } from './version.js';
import { emitJson, setJsonMode, warn } from './output.js';
import {
  pendingUpdate,
  readCheckCache,
  refreshCheckCache,
  updateCheckDisabled,
} from './lib/updateCheck.js';
import { runLogin } from './commands/login.js';
import { runWhoami } from './commands/whoami.js';
import { runInit } from './commands/init.js';
import { runProvision } from './commands/provision.js';
import { runDeploy } from './commands/deploy.js';
import { runPreview } from './commands/preview.js';
import { runState } from './commands/state.js';
import { runDev } from './commands/dev.js';
import { runMigrate } from './commands/migrate.js';
import { runSecret } from './commands/secret.js';
import { runDeploys, runLogs, runRedeploy } from './commands/apps.js';
import { runRollback } from './commands/rollback.js';
import { runDelete } from './commands/delete.js';
import { runProfile } from './commands/profile.js';
import { runPreflight } from './commands/preflight.js';

/**
 * Action wrappers are async (and the file ends with parseAsync) so
 * commander's pre/postAction hooks bracket the command instead of firing
 * around a fire-and-forget promise. The "server refused this CLI version"
 * case never reaches here — the HTTP client hard-stops with CLI_OUTDATED
 * (see output.ts exitCliOutdated); everything else keeps the historical
 * message-to-stderr, exit-1 contract.
 */
function run<T>(fn: (opts: T) => Promise<void>) {
  return async (opts: T): Promise<void> => {
    try {
      await fn(opts);
    } catch (err) {
      process.stderr.write(`${(err as Error).message}\n`);
      process.exit(1);
    }
  };
}

/** Wrapper for commands that take one positional arg before options. */
function runArg<A, T>(fn: (arg: A, opts: T) => Promise<void>) {
  return async (arg: A, opts: T): Promise<void> => {
    try {
      await fn(arg, opts);
    } catch (err) {
      process.stderr.write(`${(err as Error).message}\n`);
      process.exit(1);
    }
  };
}

const program = new Command();

program
  .name('ghosty')
  .description('Build and deploy internal company apps — from a short interview to a live URL.')
  .version(cliVersion());

// Update awareness: before a command runs, consult the throttled cache. In
// --json mode agents get a LEADING event line (never after the final ready
// line — agents treat the last JSON line as the ready object); in human mode
// the notice is queued so it prints after the command's own output. After the
// command, refresh the cache (at most one registry hit per 24h, silent).
let queuedUpdateNotice: string | null = null;

program.hook('preAction', (_thisCommand, actionCommand) => {
  const json = Boolean(actionCommand.opts<{ json?: boolean }>().json);
  setJsonMode(json);
  if (updateCheckDisabled()) return;
  const pending = pendingUpdate(readCheckCache(), cliVersion());
  if (!pending) return;
  if (json) {
    emitJson({ event: 'cli-update', current: pending.current, latest: pending.latest });
  } else {
    queuedUpdateNotice = `A newer ghosty CLI is available (${pending.current} → ${pending.latest}): npm i -g ghosty-cli@latest`;
  }
});

program.hook('postAction', async () => {
  if (queuedUpdateNotice) {
    warn({ json: false, productName: productName() }, queuedUpdateNotice);
    queuedUpdateNotice = null;
  }
  await refreshCheckCache();
});

program
  .command('login')
  .description('Sign in via your browser (at getghosty.dev by default)')
  .option('--admin-url <url>', 'your company admin URL (e.g. https://admin.<company-domain>)')
  .option('--company <slug>', 'sign in to a specific workspace by slug (picked at getghosty.dev)')
  .option('--dir <path>', "app folder to read the admin URL from (default: current folder)")
  .option('--no-browser', 'print the sign-in URL instead of opening a browser')
  .option('--json', 'machine-readable output')
  .action(run(runLogin));

program
  .command('whoami')
  .description('Show the signed-in builder')
  .option('--admin-url <url>', 'use a specific profile')
  .option('--json', 'machine-readable output')
  .action(run(runWhoami));

program
  .command('init')
  .description('Scaffold a new app in this folder (short interview)')
  .option('--slug <slug>', 'URL name, kebab-case')
  .option('--name <name>', 'app display name')
  .option('--description <text>', 'one-line description')
  .option('--backend <kind>', 'backend kind: node | python')
  // Hidden: apps scaffold with NO database; it's enabled on demand later by
  // building a data model. This flag only exists as an escape hatch.
  .addOption(new Option('--database', 'force-enable a database at scaffold time').hideHelp())
  // Hidden for the same reason: file storage is enabled on demand later by
  // building a feature that stores files.
  .addOption(new Option('--file-storage', 'force-enable file storage at scaffold time').hideHelp())
  .option('--dir <path>', 'target directory (default: current folder)')
  .option('-y, --yes', 'accept defaults, no prompts')
  .option('--json', 'machine-readable output (implies --yes)')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runInit));

program
  .command('provision')
  .description('Create everything this app needs on the platform (repo, build, URL, database)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--force', 're-run blocks that are already ready')
  .option('--json', 'machine-readable output (JSON lines)')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runProvision));

program
  .command('deploy')
  .description('Build and deploy your app (commits + pushes your changes, then ships it)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('-m, --message <text>', 'commit message for this deploy')
  .option('--json', 'machine-readable output (JSON lines)')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runDeploy));

program
  .command('preview')
  .argument('[name]', 'optional name for the preview (used for a fresh preview branch)')
  .description('Push your current work to a live preview URL (without touching production)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output (JSON lines)')
  .option('--admin-url <url>', 'use a specific profile')
  .action(runArg(runPreview));

program
  .command('state')
  .description('Show platform resource status for this app')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runState));

program
  .command('dev')
  .description('Run the app locally (frontend + backend, with your dev database)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runDev));

program
  .command('migrate')
  .argument('<name>', 'short snake_case label for the change (e.g. add_invoice_notes)')
  .description("Apply a database schema change to your personal dev schema")
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(runArg(runMigrate));

program
  .command('secret')
  .argument('<name>', 'kebab-case secret name (e.g. openai-api-key)')
  .description('Store a secret for this app and bind it to the service')
  .option('--value <v>', 'the secret value (omit to be prompted; never echoed)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(runArg(runSecret));

program
  .command('deploys')
  .description('List recent deploys for this app')
  .option('--limit <n>', 'max deploys to show (default 20)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runDeploys));

program
  .command('logs')
  .description('Show recent production logs for this app')
  .option('--limit <n>', 'max log lines to show (default 100)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runLogs));

program
  .command('redeploy')
  .description('Rebuild and redeploy this app from the latest main')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runRedeploy));

program
  .command('rollback')
  .description("Put a previous successful deploy's version back live (no rebuild)")
  .option('--to <shaOrDeployId>', 'deploy id or commit-sha prefix (default: the previous version)')
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runRollback));

program
  .command('delete')
  .argument('<slug>', 'the app to delete')
  .description('Permanently delete an app from the platform (your GitHub repository is kept)')
  .option('--yes', 'confirm the deletion (required — this command never prompts)')
  .option('--no-wait', "don't wait for the cleanup to finish")
  .option('--dir <path>', 'app directory (default: current folder)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(runArg(runDelete));

program
  .command('profile')
  .description('Update your builder profile')
  .option('--github <username>', 'your GitHub username (grants you push access on app repos)')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runProfile));

program
  .command('preflight')
  .description('Check your machine and connection are ready')
  .option('--json', 'machine-readable output')
  .option('--admin-url <url>', 'use a specific profile')
  .action(run(runPreflight));

// Branded banner for bare `ghosty` / help invocations (human output only).
const args = process.argv.slice(2);
const helpOnly = args.length === 0 || args[0] === 'help' || args[0] === '--help' || args[0] === '-h';
if (helpOnly && process.stdout.isTTY) {
  printBanner(productName());
}
if (args.length === 0) {
  program.outputHelp();
  process.exit(0);
}

await program.parseAsync();
